import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../api';
import {
  DEFAULT_OPERATOR_RIG_SETTINGS,
  loadLocalRigSettings,
  normalizeRigSettings,
  saveLocalRigSettings
} from './rigSettings';
import { isNativeRigBridgeAvailable } from './nativeBridge';
import {
  createRigBridgeConnection,
  sendRigBridgeMessage,
  RECONNECT_MS
} from './rigBridgeTransport';

export function useRigBridge(operatorCallsign) {
  const callsign = String(operatorCallsign || '').trim().toUpperCase();
  const [settings, setSettings] = useState(DEFAULT_OPERATOR_RIG_SETTINGS);
  const [connectionState, setConnectionState] = useState('idle');
  const [backends, setBackends] = useState({});
  const [rigState, setRigState] = useState(null);
  const [lastDecode, setLastDecode] = useState(null);
  const [pendingQso, setPendingQso] = useState(null);
  const [error, setError] = useState('');
  const transportRef = useRef(null);
  const isNativeRef = useRef(false);
  const reconnectTimerRef = useRef(null);
  const settingsRef = useRef(DEFAULT_OPERATOR_RIG_SETTINGS);

  const applySettings = useCallback((nextSettings) => {
    const normalized = normalizeRigSettings(nextSettings);
    settingsRef.current = normalized;
    setSettings(normalized);
    if (callsign) {
      saveLocalRigSettings(callsign, normalized);
    }
  }, [callsign]);

  const loadSettings = useCallback(async () => {
    if (!callsign) {
      applySettings(DEFAULT_OPERATOR_RIG_SETTINGS);
      return DEFAULT_OPERATOR_RIG_SETTINGS;
    }

    let merged = loadLocalRigSettings(callsign);
    try {
      const response = await apiFetch(`/api/operators/${encodeURIComponent(callsign)}/rig-settings`);
      if (response.ok) {
        const data = await response.json();
        merged = normalizeRigSettings(data.settings || merged);
        saveLocalRigSettings(callsign, merged);
      }
    } catch (loadError) {
      console.warn('Unable to load rig settings from server, using local copy', loadError);
    }

    applySettings(merged);
    return merged;
  }, [applySettings, callsign]);

  const handleBridgeMessage = useCallback((event) => {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }

    if (message.type === 'bridge_ready' || message.type === 'hello_ack') {
      setConnectionState('connected');
    }
    if (message.type === 'backends') {
      setBackends(message.backends || {});
    }
    if (message.type === 'rig_state') {
      setRigState(message.rigState || null);
    }
    if (message.type === 'wsjtx_decode') {
      setLastDecode(message);
    }
    if (message.type === 'wsjtx_qso') {
      setPendingQso({
        ...message.contact,
        autoLog: Boolean(message.contact?.autoLog)
      });
    }
    if (message.type === 'error') {
      setError(message.error || 'Rig Bridge error');
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (transportRef.current) {
      if (isNativeRef.current) {
        transportRef.current.close?.();
      } else {
        transportRef.current.close();
      }
      transportRef.current = null;
    }
    isNativeRef.current = false;
    setConnectionState('idle');
  }, []);

  const connect = useCallback(async () => {
    if (!callsign) {
      disconnect();
      return;
    }

    const activeSettings = settingsRef.current.bridgeUrl
      ? settingsRef.current
      : await loadSettings();

    disconnect();
    setConnectionState('connecting');
    setError('');

    const { transport, isNative } = createRigBridgeConnection({
      callsign,
      settingsRef,
      onOpen: () => {
        setConnectionState('connected');
        if (isNative) {
          sendRigBridgeMessage(transport, true, {
            type: 'hello',
            callsign,
            settings: activeSettings
          });
        }
      },
      onMessage: handleBridgeMessage,
      onClose: () => {
        if (transportRef.current === transport) {
          transportRef.current = null;
        }
        setConnectionState('disconnected');
        reconnectTimerRef.current = setTimeout(() => connect(), RECONNECT_MS);
      },
      onError: (connectError) => {
        setConnectionState('error');
        const nativeHint = isNativeRigBridgeAvailable()
          ? 'Native rig bridge error — check Settings → FLRig host or WSJT-X UDP'
          : 'Rig Bridge connection failed — is cq-rush-rig-bridge running on this computer?';
        setError(connectError?.message || nativeHint);
        reconnectTimerRef.current = setTimeout(() => connect(), RECONNECT_MS);
      }
    });

    if (!transport) {
      setConnectionState('error');
      reconnectTimerRef.current = setTimeout(() => connect(), RECONNECT_MS);
      return;
    }

    transportRef.current = transport;
    isNativeRef.current = isNative;
  }, [callsign, disconnect, handleBridgeMessage, loadSettings]);

  const saveSettings = useCallback(async (nextSettings) => {
    const normalized = normalizeRigSettings(nextSettings);
    applySettings(normalized);

    if (callsign) {
      try {
        await apiFetch(`/api/operators/${encodeURIComponent(callsign)}/rig-settings`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ settings: normalized })
        });
      } catch (saveError) {
        console.warn('Unable to save rig settings to server', saveError);
      }
    }

    sendRigBridgeMessage(transportRef.current, isNativeRef.current, {
      type: 'update_settings',
      settings: normalized
    });

    if (!transportRef.current) {
      connect();
    }
  }, [applySettings, callsign, connect]);

  const clearPendingQso = useCallback(() => {
    setPendingQso(null);
  }, []);

  useEffect(() => {
    if (!callsign) {
      disconnect();
      setRigState(null);
      setBackends({});
      setPendingQso(null);
      return undefined;
    }

    loadSettings().then(() => connect());

    return () => disconnect();
  }, [callsign, connect, disconnect, loadSettings]);

  return {
    settings,
    connectionState,
    backends,
    rigState,
    lastDecode,
    pendingQso,
    error,
    saveSettings,
    reconnect: connect,
    clearPendingQso,
    isNativeBridge: isNativeRigBridgeAvailable()
  };
}
